/* ============================================================
   Stems+ — storefront subscriber detection & UI
   Fig & Bloom
   ------------------------------------------------------------
   What this does:
   1. Decides whether the current visitor is a known email subscriber
      (best-effort, via Klaviyo onsite identity, with an optional
      server-verified upgrade path).
   2. Sets data-sp-state="member" | "guest" on <html>, which the CSS
      uses to show the right badge / widget copy.
   3. (Optional, gated) Swaps the PDP gallery to subscriber imagery
      when a product has stems_plus.subscriber_media set.

   Honesty rule: we only render the "member" state when we are
   CONFIDENT the visitor is subscribed. Everyone else sees the
   "guest" invite — never a promise.
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.STEMS_PLUS_CONFIG || {};
  var ROOT = document.documentElement;

  // Avoid flashing the wrong state: start in a neutral state.
  ROOT.setAttribute("data-sp-state", "unknown");

  /* ---------- detection ---------------------------------- */

  // Strategy, in order of confidence:
  // (A) Server-verified consent via App Proxy  — most accurate (opt-in)
  // (B) Klaviyo onsite isIdentified()          — "they gave us their email"
  // (C) __kla_id cookie presence               — fallback for older onsite
  // If none resolve to true, we treat the visitor as a guest.

  function setState(isMember) {
    ROOT.setAttribute("data-sp-state", isMember ? "member" : "guest");
    if (isMember && CFG.enableImageSwap !== false) {
      try { swapSubscriberImages(); } catch (e) { /* never break the page */ }
    }
  }

  function hasKlaviyoCookie() {
    return /(^|;)\s*__kla_id=/.test(document.cookie);
  }

  // (A) Optional App Proxy check. Set window.STEMS_PLUS_CONFIG.verifyUrl
  //     to a Shopify App Proxy path (e.g. "/apps/stems-plus/status")
  //     that returns { "subscribed": true|false }. Leave unset to skip.
  function verifyViaProxy() {
    if (!CFG.verifyUrl) return Promise.reject("no-proxy");
    return fetch(CFG.verifyUrl, { credentials: "include", headers: { "Accept": "application/json" } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject("bad-status"); })
      .then(function (d) { return !!(d && d.subscribed); });
  }

  // (B) Klaviyo onsite. Newer onsite exposes window.klaviyo with an
  //     async isIdentified(); older builds use the _learnq queue.
  function verifyViaKlaviyo() {
    return new Promise(function (resolve, reject) {
      var tries = 0;
      (function wait() {
        var k = window.klaviyo;
        if (k && typeof k.isIdentified === "function") {
          try {
            var out = k.isIdentified();
            // isIdentified may return a boolean or a Promise depending on version
            if (out && typeof out.then === "function") {
              out.then(function (v) { resolve(!!v); }).catch(reject);
            } else {
              resolve(!!out);
            }
          } catch (e) { reject(e); }
          return;
        }
        if (tries++ > 40) return reject("klaviyo-timeout"); // ~4s
        setTimeout(wait, 100);
      })();
    });
  }

  function detect() {
    verifyViaProxy()
      .then(setState)
      .catch(function () {
        verifyViaKlaviyo()
          .then(setState)
          .catch(function () { setState(hasKlaviyoCookie()); });
      });
  }

  /* ---------- PDP image swap (gated) --------------------- */
  // Inert unless a <script data-sp-media> block is present on the page
  // (rendered by stems-plus.liquid only when the product has
  // stems_plus.subscriber_media set). Safe no-op otherwise.

  function swapSubscriberImages() {
    var dataEl = document.querySelector("script[data-sp-media]");
    if (!dataEl) return;
    var urls;
    try { urls = JSON.parse(dataEl.textContent || "[]"); } catch (e) { return; }
    if (!urls || !urls.length) return;

    // CFG.gallerySelector lets you target your theme's gallery <img>s.
    var sel = CFG.gallerySelector || ".product__media img, .product-gallery img";
    var imgs = document.querySelectorAll(sel);
    imgs.forEach(function (img, i) {
      if (urls[i]) {
        img.removeAttribute("srcset");
        img.src = urls[i];
      }
    });
  }

  /* ---------- widget dismissal ---------------------------- */
  // The × button hides the floating widget for the rest of the session
  // (sessionStorage, so it returns on the next visit). The CSS rule
  // [data-sp-widget][data-sp-dismissed="1"] does the actual hiding.

  var DISMISS_KEY = "sp-widget-dismissed";

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function storageSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode etc. */ }
  }

  function initDismiss() {
    var widget = document.querySelector("[data-sp-widget]");
    if (!widget) return;
    if (storageGet(DISMISS_KEY) === "1") {
      widget.setAttribute("data-sp-dismissed", "1");
      return;
    }
    var btn = widget.querySelector("[data-sp-dismiss]");
    if (!btn) return;
    btn.addEventListener("click", function () {
      widget.setAttribute("data-sp-dismissed", "1");
      storageSet(DISMISS_KEY, "1");
    });
  }

  /* ---------- scroll reveal ------------------------------ */
  // Keep the pill hidden above the fold (so it never covers a page CTA on
  // load) and slide it up into view once the visitor scrolls down past a
  // small threshold. Hides again near the top.

  function initScrollReveal() {
    var widget = document.querySelector("[data-sp-widget]");
    if (!widget) return;
    var SHOW_AT = 300; // px scrolled before the pill may show at all
    var DELTA = 6; // ignore scroll jitter / rubber-banding
    var ticking = false;
    var lastY = window.pageYOffset || 0;

    // Direction-aware: scrolling DOWN slides the pill away so it never sits
    // on top of product cards while browsing; scrolling UP brings it back.
    // Near the top it stays hidden, so it can't cover an above-the-fold CTA.
    function update() {
      ticking = false;
      var y = window.pageYOffset || 0;
      var diff = y - lastY;

      if (y <= SHOW_AT) {
        widget.classList.remove("sp-visible");
        lastY = y;
        return;
      }
      if (Math.abs(diff) < DELTA) return;
      lastY = y;

      if (diff > 0) {
        widget.classList.remove("sp-visible");
      } else {
        widget.classList.add("sp-visible");
      }
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
  }

  /* ---------- go ----------------------------------------- */

  function init() {
    initDismiss();
    initScrollReveal();
    detect();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
